import { UploadFile } from 'antd';
import { getAntdApp } from '@/utils/antdApp';
import axiosInstance from './axiosInstance';
import i18n from '../../i18n';
import type { Response } from './axiosInstance';

type Sorting = {
    by: string;
    asc: boolean;
};

export type FileInfo = {
    children: FileInfo[];
    key: string;
    numDirs: number;
    numFiles: number;
    sorting: Sorting;
    path: string;
    name: string;
    size: number;
    extension: string;
    modified: Date;
    mode: number; // os.FileMode equivalent in TypeScript
    isDir: boolean;
    isSymlink: boolean;
    isIgnore: boolean;
    type: string;
    subtitles?: string[];
    content?: string;
    checksums?: { [key: string]: string };
    token?: string;
};


export type WorkspaceRoot = {
    id: string;
    name: string;
    path: string;
    kind: 'home' | 'workspace';
    writable: boolean;
    isDefault: boolean;
    source: string;
    status: string; // ok | missing | denied
    order: number;
};

export type RootsResponse = {
    roots: WorkspaceRoot[];
    defaultRootId: string;
    homePath: string;
};

export const getRoots = async (): Promise<Response<RootsResponse> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<RootsResponse>>(`/resource/roots`);
        return response?.data;
    } catch (e) {
        return undefined;
    }
};

export type GetFileResourceResponse = {
    baseHome: string;
} & FileInfo

export const getFileResource = async (path?: string, includeHidden?: boolean): Promise<Response<GetFileResourceResponse> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<GetFileResourceResponse>>(`/resource/file${path ? path : ""}`, {
            params: { includeHidden },
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response?.data;
    } catch (e) {
        return undefined;
    }
};

// same as getFileResource but with a different endpoint
// this api have multiple path params
// so it can establish a full path for the file
export const getFileResourceList = async (params: { paths: string[], target?: string, includeHidden?: boolean }): Promise<Response<GetFileResourceResponse> | undefined> => {
    try {
        const response = await axiosInstance.post<Response<GetFileResourceResponse>>(`/resource/list`, params, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response?.data;
    } catch (e) {
        console.log(e)
        return undefined;
    }
};

export type UploadFileResourceResponse = {
    successFiles: FileInfo[];
    conflictFiles: string[];
    hasConflicts: boolean;
}

export const uploadFileResource = async (files: UploadFile[], path: string, force: boolean = false): Promise<Response<UploadFileResourceResponse> | undefined> => {
    const formData = new FormData();
    files.forEach(file => {
        console.log(file);
        if (file.originFileObj) {
            formData.append('files', file.originFileObj);
        }
    });
    var encodedPath = encodeURIComponent(path);
    try {
        const response = await axiosInstance.post<Response<UploadFileResourceResponse>>(`/resource/file?path=${encodedPath}&force=${force}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch {
        return undefined;
    }
};

export const getRawFileResource = async (path?: string): Promise<string | undefined> => {
    try {
        const response = await axiosInstance.get<string>(`/resource/raw${path ? path : ""}`, {
            responseType: 'text'
        });
        return response?.data;
    } catch (e) {
        getAntdApp().message.error(i18n.t('api_file.file_cannot_be_opened', { ns: 'common' }));
        return undefined;
    }
};

export type GetRawFileChunkRequest = {
    path: string;
    offset: number;
    limit: number;
};

export type GetRawFileChunkResponse = {
    path: string;
    offset: number;
    limit: number;
    bytesRead: number;
    totalSize: number;
    isLast: boolean;
    content: string;
};

export type SearchRawFileRequest = {
    path: string;
    query: string;
    caseSensitive?: boolean;
    wholeWord?: boolean;
    regex?: boolean;
    limit?: number;
    cursor?: string;
};

export type SearchRawFileMatch = {
    lineNumber: number;
    startColumn: number;
    endColumn: number;
    offset: number;
    preview?: string;
};

export type SearchRawFileResponse = {
    path: string;
    query: string;
    total: number;
    nextCursor?: string;
    matches: SearchRawFileMatch[];
};

export const getRawFileResourceChunk = async (params: GetRawFileChunkRequest): Promise<GetRawFileChunkResponse | undefined> => {
    try {
        const response = await axiosInstance.get<GetRawFileChunkResponse>(`/resource/raw/chunk`, {
            params,
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response?.data;
    } catch (e) {
        getAntdApp().message.error(i18n.t('api_file.file_cannot_be_opened', { ns: 'common' }));
        return undefined;
    }
};

export const searchRawFileResource = async (params: SearchRawFileRequest): Promise<SearchRawFileResponse | undefined> => {
    try {
        const response = await axiosInstance.get<SearchRawFileResponse>(`/resource/raw/search`, {
            params,
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response?.data;
    } catch (e) {
        getAntdApp().message.error(i18n.t('api_file.file_cannot_be_opened', { ns: 'common' }));
        return undefined;
    }
};

export const saveFileResource = async (path: string, content: string): Promise<Response<{}>> => {
    var encodedPath = encodeURIComponent(path);
    try {
        const response = await axiosInstance.put<Response<{}>>(`/resource/file?path=${encodedPath}`, content, {
            headers: {
                'Content-Type': 'text/plain'
            }
        });
        return response.data;
    } catch (e: any) {
        return { ok: false, message: e?.response?.data.message, data: {} };
    }
}


export const deleteFileResource = async (pathList: string[]): Promise<Response<undefined>> => {
    try {
        const response = await axiosInstance.delete<Response<undefined>>(`/resource/file`, {
            headers: {
                'Content-Type': 'application/json'
            },
            data: {
                pathList
            }
        });
        return response.data;
    } catch (e: any) {
        return { ok: false, message: e?.response?.data.message, data: undefined };
    }
};

type createFileResourceResponse = {
    path: string;
    isDir: boolean;
    oldPath: string;
}

export const renameFileResource = async (path: string, newName: string): Promise<Response<createFileResourceResponse | undefined>> => {
    try {
        const response = await axiosInstance.post<Response<createFileResourceResponse | undefined>>('/resource/rename', {
            path,
            new_name: newName
        }, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (e: any) {
        return { ok: false, message: e?.response?.data.message, data: undefined };
    }
};

type copyFileResponse = { [key: string]: string }

export const copyFileResource = async (fileCopyList: { src: string, dst: string, isDir: boolean }[]): Promise<Response<copyFileResponse | undefined>> => {
    try {
        const response = await axiosInstance.post<Response<copyFileResponse | undefined>>(`/resource/copy`, {
            fileCopyList
        }, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (e: any) {
        return { ok: false, message: e?.response?.data.message, data: undefined };
    }
};

type moveFileResourceResponse = Array<{
    src: string;
    dst: string;
    isDir: boolean;
}>

export const moveFileResource = async (sourceList: string[], dest: string): Promise<Response<moveFileResourceResponse | undefined>> => {
    try {
        const response = await axiosInstance.post<Response<moveFileResourceResponse | undefined>>(`/resource/move`, {
            source_list: sourceList,
            dest
        }, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (e: any) {
        return { ok: false, message: e?.response?.data.message, data: undefined };
    }
};

export const getListFileResource = async (pathList: string[], layer?: number): Promise<Response<FileInfo[]> | undefined> => {
    try {
        const response = await axiosInstance.post<Response<FileInfo[]>>(`/resource/list`, {
            pathList,
            layer
        }, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (e) {
        return undefined;
    }
}

export const getRawFileResourceInBlob = async (path?: string): Promise<Blob | undefined> => {
    try {
        const response = await axiosInstance.get<Blob>(`/resource/raw${path ? path : ""}`, {
            responseType: 'blob'
        });
        return response?.data;
    } catch (e) {
        getAntdApp().message.error(i18n.t('api_file.file_cannot_be_opened', { ns: 'common' }));
        return undefined;
    }
};

export const batchDownloadResource = async (paths: string[], zipName = 'download'): Promise<Blob | undefined> => {
    try {
        const response = await axiosInstance.post<Blob>(`/resource/batch-download`, { paths, zipName }, {
            responseType: 'blob'
        });
        return response?.data;
    } catch (e) {
        return undefined;
    }
};

export const getDemoIntialization = async (override: boolean = false): Promise<Response<string[]> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<string[]>>(`/resource/demo`, {
            params: { override }
        });
        return response?.data;
    } catch (e) {
        return undefined;
    }
};

export interface DiskUsage {
    id: number;
    username: string;
    email: string;
    home_dir: string;
    used_bytes: number;
    status: string;
    error_message: string;
    max_storage_bytes: number;
    collected_at: string;
    created_at: string;
    updated_at: string;
}

export const getDiskUsage = async (): Promise<DiskUsage | null> => {
    try {
        const response = await axiosInstance.get<Response<DiskUsage | null>>('/disk-usage');
        return response?.data?.data ?? null;
    } catch (e) {
        return null;
    }
};
